import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useFetchResponses, useInventoryRows } from '../../../hooks/assessment-hooks';
import {
  Assessment,
  AssessmentQuestion,
  AssessmentResponse,
  AssessmentResponseSet,
  normalizeOptions,
} from '../../../support/assessments';
import {
  buildReport,
  getReportConfig,
  getScoringConfig,
  scoreAssessment,
} from '../../../support/assessmentScoring';
import { computeAiEraReport } from '../../../support/aiEraReport/compute';
import AssessmentResultsSummary from '../../Assessments/results/AssessmentResultsSummary';
import AiEraReportView from '../../Assessments/results/AiEraReportView';
import ImportedWidgetsView from '../../DataImport/ImportedWidgetsView';

/**
 * Admin review of a single submitted response set: the computed maturity summary (recomputed from
 * the stored answers, so it reflects the current scoring config even if the assessment's config
 * changed after submission) plus the raw answer-by-answer table.
 */

type Props = {
  assessment: Assessment;
  allQuestions: AssessmentQuestion[];
  sectionKeyById: Record<number, string>;
  responseSet: AssessmentResponseSet;
  onBack: () => void;
};

/** Render a stored answer value as readable text, given its question for option-label lookup. */
const formatResponse = (response: AssessmentResponse, question?: AssessmentQuestion): string => {
  const value = response.response;
  if (value === null || value === undefined || value === '') return '—';

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  // Choice answers store the option value; show the option's label when we still have the question.
  const options = question ? normalizeOptions(question.response_options) : [];
  const labelFor = (v: any) => options.find((o) => o.value === String(v))?.label ?? String(v);

  if (Array.isArray(value)) return value.map(labelFor).join(', ') || '—';

  if (typeof value === 'object') {
    // Data-import answers reference a persisted dataset + its generated widgets.
    if (typeof value.datasetId === 'number') {
      return `Imported dataset #${value.datasetId} · ${value.widgetCount ?? 0} widget(s)`;
    }
    // Upload / Salesforce-lookup answers.
    return value.file_name || value.name || value.id || JSON.stringify(value);
  }

  return labelFor(value);
};

const ResponseSetDetail: React.FC<Props> = ({
  assessment,
  allQuestions,
  sectionKeyById,
  responseSet,
  onBack,
}) => {
  const { data: responses = [], isLoading } = useFetchResponses(responseSet.id);

  const questionByKey = useMemo(() => {
    const map: Record<string, AssessmentQuestion> = {};
    allQuestions.forEach((q) => {
      map[q.key] = q;
    });
    return map;
  }, [allQuestions]);

  // Answers keyed by question key — the shape the scorer and `visible_when` rules use. The response
  // serializer adds `question_key`, so we key off that rather than the raw question id.
  const answersByKey = useMemo(() => {
    const map: Record<string, any> = {};
    (responses as AssessmentResponse[]).forEach((r) => {
      if (r.question_key) map[r.question_key] = r.response;
    });
    return map;
  }, [responses]);

  const config = useMemo(() => getScoringConfig(assessment), [assessment]);
  const result = useMemo(
    () => scoreAssessment(config, allQuestions, sectionKeyById, answersByKey),
    [config, allQuestions, sectionKeyById, answersByKey]
  );
  const report = useMemo(() => buildReport(result, getReportConfig(assessment)), [result, assessment]);

  // Dataset ids referenced by any `data_import` answer, so their widgets can be shown below the table.
  const importedDatasetIds = useMemo(
    () =>
      (responses as AssessmentResponse[])
        .map((r) => r.response?.datasetId)
        .filter((id): id is number => typeof id === 'number'),
    [responses]
  );

  // Recompute the AI-Era report from the stored answers — the same adapter +
  // engine the fill surface runs, so admin review matches the respondent's view.
  // The Cisco inventory it needs comes from the first `data_import` dataset.
  const { data: inventoryRows = [] } = useInventoryRows(importedDatasetIds[0]);
  const aiEra = useMemo(
    () => computeAiEraReport({ assessment, answersByKey, inventoryRows }),
    [assessment, answersByKey, inventoryRows]
  );

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ mb: 2 }}>
        All Submissions
      </Button>

      <Box mb={2}>
        <Typography variant="h6">{assessment.name}</Typography>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={0.5}>
          <Typography variant="body2" color="text.secondary">
            {responseSet.respondent || 'Anonymous'}
          </Typography>
          <Chip size="small" label={responseSet.status} />
          {!!responseSet.submitted_at && (
            <Typography variant="caption" color="text.secondary">
              Submitted {new Date(responseSet.submitted_at).toLocaleString()}
            </Typography>
          )}
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {result && (
            <Box mb={3}>
              <AssessmentResultsSummary result={result} report={report} />
            </Box>
          )}

          {aiEra?.reportData && (
            <Box mb={3}>
              <AiEraReportView report={aiEra.reportData} config={assessment.metadata?.reportLayout} />
            </Box>
          )}

          <Paper variant="outlined">
            <Box p={2}>
              <Typography variant="subtitle1">Responses</Typography>
              <Typography variant="body2" color="text.secondary">
                {responses.length} answer{responses.length === 1 ? '' : 's'} recorded.
              </Typography>
            </Box>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Question</TableCell>
                    <TableCell>Answer</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(responses as AssessmentResponse[]).map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ verticalAlign: 'top', width: '55%' }}>
                        <Typography variant="body2">{r.question_text || r.question_key}</Typography>
                        {!!r.question_key && (
                          <Typography variant="caption" color="text.secondary">
                            {r.question_key}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        {formatResponse(r, r.question_key ? questionByKey[r.question_key] : undefined)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {importedDatasetIds.map((datasetId) => (
            <Paper variant="outlined" sx={{ mt: 3, p: 2 }} key={datasetId}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Imported data — dataset #{datasetId}
              </Typography>
              <ImportedWidgetsView datasetId={datasetId} />
            </Paper>
          ))}
        </>
      )}
    </Box>
  );
};

export default ResponseSetDetail;
